import { useEffect, useRef, useState, useCallback } from 'react';
import Field from '../../components/common/Field';
import Avatar from '../../components/common/Avatar';
import Modal from '../../components/common/Modal';
import useEventsStore from '../../store/useEventsStore';
import { showToast } from '../../store/useToastStore';
import { isValidEmail } from '../../utils/email';
import { donorIdOf, findParentById } from '../../api/services/fundraising.service';
import { fmt, todayISO } from '../../utils/formatters';
import { DONATION_SOURCES, reasonTagOptions, reasonTagLabel } from '../../utils/constants';
import { avatarFor, initialsOf } from '../../utils/avatar';
import { IconChevron } from '../../components/icons';
import useClickOutside from '../../hooks/useClickOutside';

/* openCount bumps every time the form is (re)opened: parent/source/reason reset
   to defaults (or to `editing`'s values), while amount/note/date persist until
   saved — same as the prototype.
   parents: the class roster as the Parents page lists it (roomParentsService
   .getAllParents) — the donation is recorded against donorIdOf(parent).
   editing: a normalized donation to update, or null to log a new one.
   onSave(payload) → true closes.
   emailFor(payload) → { to, build() } — the confirmation email a new donation
   sends (see FundraisingPage), rendered here by Preview email.
   gmailConnected: whether that email can actually go out on save. */
export default function DonationForm({ open, openCount, onClose, parents, editing, onSave, emailFor, gmailConnected }) {
  const events = useEventsStore((s) => s.events);

  const [donorId, setDonorId] = useState('');
  const [amount, setAmount] = useState('');
  const [source, setSource] = useState('Venmo');
  const [date, setDate] = useState('');
  const [reason, setReason] = useState('supplies');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const pickerRef = useRef(null);

  useClickOutside(pickerRef, useCallback(() => setPickerOpen(false), []));

  const reasons = reasonTagOptions(events);

  useEffect(() => {
    if (openCount > 0) {
      if (editing) {
        setDonorId(editing.userId || '');
        setAmount(editing.amount ? String(editing.amount) : '');
        /* API stores source as its enum and reason as a plain label — map back to our options */
        const src = DONATION_SOURCES.find(
          (s) => s.api === editing.source || s.value.toLowerCase() === String(editing.source || '').toLowerCase()
        );
        setSource(src ? src.value : 'Other');
        const rsn = reasons.find((t) => t.label.toLowerCase() === String(editing.reason || '').toLowerCase());
        setReason(rsn ? rsn.id : 'other');
        setDate(editing.date || '');
        setNote(editing.note || '');
      } else {
        setDonorId(donorIdOf(parents[0]));
        setSource('Venmo');
        setReason('supplies');
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openCount]);

  const parent = findParentById(parents, donorId);
  const parentName = parent?.name || editing?.parentName || '';
  const av = avatarFor(parentName, []);
  const amountNum = parseFloat(amount) || 0;

  /* the donation as the form holds it — what Save sends and Preview renders */
  const payloadNow = () => ({
    userId: donorId,
    amount: amountNum,
    source: DONATION_SOURCES.find((s) => s.value === source)?.api || 'other',
    dateReceived: date || todayISO(),
    reason: reasonTagLabel(reason, events),
    note: note.trim(),
  });

  /* Preview email: the confirmation this donation would send, with whatever is
     in the form right now */
  const [preview, setPreview] = useState(null); // { to, subject, html }
  const [previewLoading, setPreviewLoading] = useState(false);
  const openPreview = async () => {
    if (!emailFor) return;
    setPreviewLoading(true);
    try {
      const email = emailFor(payloadNow());
      const { subject, html } = await email.build();
      setPreview({ to: email.to, subject, html });
    } catch (err) {
      showToast(err.message);
    } finally {
      setPreviewLoading(false);
    }
  };

  const save = async () => {
    if (!donorId) { window.alert('Select a parent.'); return; }
    if (!amountNum || amountNum <= 0) { window.alert('Enter an amount greater than $0.'); return; }
    setSaving(true);
    const ok = await onSave(payloadNow());
    setSaving(false);
    if (ok) {
      setAmount('');
      setNote('');
      setDate('');
    }
  };

  return (
    <div className={`inline-form ${open ? 'on' : ''}`}>
      <div className="email-grid">
        <div>
          <Field label="Parent" style={{ marginBottom: 10 }}>
            <div className="rich-select" ref={pickerRef}>
              <button type="button" className="rich-select-btn" onClick={() => setPickerOpen((o) => !o)}>
                <Avatar src={parent?.picture} initials={parentName ? av.initials : '?'} color={av.color} size={24} fontSize={9} />
                <span>{parentName || 'Select a parent'}</span>
                <IconChevron />
              </button>
              <div className={`rich-select-menu ${pickerOpen ? 'open' : ''}`}>
                {parents.map((p) => (
                  <div
                    className="rich-select-item"
                    key={p.id}
                    onClick={() => { setDonorId(donorIdOf(p)); setPickerOpen(false); }}
                  >
                    <Avatar src={p.picture} initials={initialsOf(p.name)} color={avatarFor(p.name, []).color} size={24} fontSize={9} />
                    <span>
                      {p.name}
                      {p.onSprout ? '' : ' (email only)'}
                    </span>
                  </div>
                ))}
                {!parents.length && (
                  <div className="rich-select-item" style={{ color: 'var(--muted)', cursor: 'default' }}>
                    No parents in this class yet.
                  </div>
                )}
              </div>
            </div>
          </Field>
          <Field label="Amount ($)" style={{ marginBottom: 10 }}>
            <input type="number" value={amount} placeholder="0.00" step="0.01" min="0" onChange={(e) => setAmount(e.target.value)} />
          </Field>
          <Field label="Source" style={{ marginBottom: 10 }}>
            <div className="tag-select-row">
              {DONATION_SOURCES.map((s) => (
                <span key={s.value} className={`src-chip-btn ${source === s.value ? 'on' : ''}`} onClick={() => setSource(s.value)}>
                  {s.label}
                </span>
              ))}
            </div>
          </Field>
          <Field label="Date Received" style={{ marginBottom: 10 }}>
            <div className="date-input-wrap">
              <svg className="cal-ic" viewBox="0 0 24 24"><rect x="3" y="5" width="18" height="16" rx="2" /><path d="M3 10h18M8 3v4M16 3v4" /></svg>
              <input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
            </div>
          </Field>
          <Field label="Donation Reason" style={{ marginBottom: 10 }}>
            <div className="tag-select-row">
              {reasons.map((t) => (
                <span key={t.id} className={`src-chip-btn ${reason === t.id ? 'on' : ''}`} onClick={() => setReason(t.id)}>
                  {t.label}
                </span>
              ))}
            </div>
          </Field>
          <Field label="Note (optional)">
            <input type="text" value={note} placeholder="Extra detail, e.g. which field trip" onChange={(e) => setNote(e.target.value)} />
          </Field>
        </div>
        <div>
          <Field label="Preview" />
          <div className="celebrate-card">
            <div className="celebrate-avatar" style={{ background: av.color }}>
              {parentName ? av.initials : '?'}
            </div>
            <div className="celebrate-amt">{fmt(amountNum)}</div>
            <div className="celebrate-name">{parentName || 'Select a parent'}</div>
            <div className="celebrate-reason">
              {reasonTagLabel(reason, events)}
              {note.trim() ? ` — ${note.trim()}` : ''}
            </div>
            <div className="celebrate-note">
              <img src="/assets/icons/donation.svg" alt="" />
              <span>
                {editing
                  ? "Editing doesn't send the parent another confirmation."
                  : gmailConnected
                    ? 'The parent gets a confirmation email when you save.'
                    : 'Connect Gmail at the top right so the parent gets a confirmation email when you save.'}{' '}
                {!editing && emailFor && (
                  <button type="button" className="switch-link" disabled={previewLoading} onClick={openPreview}>
                    {previewLoading ? 'Loading…' : 'Preview email'}
                  </button>
                )}
              </span>
            </div>
          </div>
        </div>
      </div>

      <Modal open={!!preview} onClose={() => setPreview(null)} title="Confirmation email" maxWidth={680}>
        {preview && (
          <>
            <div className="don-email-meta">
              <div>
                <span>To</span>
                <b>{isValidEmail(preview.to) ? preview.to : 'No email on file, so nothing will be sent'}</b>
              </div>
              <div>
                <span>Subject</span>
                <b>{preview.subject}</b>
              </div>
            </div>
            {/* Scripts stay blocked — the email is static HTML. allow-same-origin
                because Chrome leaves a fully sandboxed srcdoc frame that's added
                after load blank. */}
            <iframe
              className="don-email-frame"
              title="Donation confirmation email preview"
              sandbox="allow-same-origin"
              srcDoc={preview.html}
            />
          </>
        )}
      </Modal>
      <div className="inline-form-actions">
        <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
        <button className="btn btn-primary" disabled={saving} onClick={save}>
          {saving ? 'Saving…' : editing ? 'Update Donation' : 'Save Donation'}
        </button>
      </div>
    </div>
  );
}
